import { memo, useState } from 'react';
import PropTypes from 'prop-types';
import RatingStars from './RatingStars';
import { addCacheBuster } from '../../utils/apiHelpers';
import './ReviewItem.css';

/**
 * ReviewItem Component
 *
 * Displays a single review with:
 * - User information (name, avatar, verified badge)
 * - Rating and date
 * - Review title and comment
 * - Review images (if any)
 * - Helpful vote button
 * - Edit/Delete actions (for own reviews)
 * - Different states (pending, rejected, etc.)
 */
const ReviewItem = memo(function ReviewItem({
  review,
  isOwnReview = false,
  currentUserId = null,
  onHelpfulClick,
  onEditClick,
  onDeleteClick,
  onImageClick,
  imageRefreshKey = null
}) {
  const [showFullComment, setShowFullComment] = useState(false);

  // Format date to relative time
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine if comment is long
  const isLongComment = review.comment && review.comment.length > 500;
  const displayComment = isLongComment && !showFullComment
    ? review.comment.slice(0, 500) + '...'
    : review.comment;

  // Determine review state class
  const getStateClass = () => {
    if (isOwnReview) {
      if (review.status === 'PENDING') return 'review-item--pending';
      if (review.status === 'REJECTED') return 'review-item--rejected';
      return 'review-item--own';
    }
    return '';
  };

  // Get status badge
  const getStatusBadge = () => {
    if (!isOwnReview) return null;

    switch (review.status) {
      case 'PENDING':
        return (
          <span className="review-item__badge review-item__badge--pending">
            ⏳ Pending Approval
          </span>
        );
      case 'REJECTED':
        return (
          <span className="review-item__badge review-item__badge--rejected">
            ❌ Not Approved
          </span>
        );
      case 'APPROVED':
        if (isOwnReview) {
          return (
            <span className="review-item__badge review-item__badge--own">
              Your Review
            </span>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <article className={`review-item ${getStateClass()}`}>
      {/* Header */}
      <div className="review-item__header">
        <div className="review-item__user">
          {/* Avatar */}
          <div className="review-item__avatar">
            {getUserInitials(review.userName)}
          </div>

          <div className="review-item__user-info">
            <div className="review-item__user-name">
              {review.userName}
              {review.isVerifiedPurchase && (
                <span className="review-item__verified" title="Verified Purchase">
                  <i className="fas fa-check-circle"></i> Verified Purchase
                </span>
              )}
            </div>
            {getStatusBadge()}
          </div>
        </div>

        <div className="review-item__meta">
          <RatingStars rating={review.rating} size="sm" />
          <span className="review-item__date">{formatDate(review.createdAt)}</span>
        </div>
      </div>

      {/* Title */}
      {review.title && (
        <h4 className="review-item__title">{review.title}</h4>
      )}

      {/* Comment */}
      {review.comment && (
        <div className="review-item__comment">
          <p>{displayComment}</p>
          {isLongComment && (
            <button
              className="review-item__read-more"
              onClick={() => setShowFullComment(!showFullComment)}
            >
              {showFullComment ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}

      {/* Images */}
      {review.images && review.images.length > 0 && (
        <div className="review-item__images">
          {review.images.slice(0, 3).map((image, index) => {
            const imageUrl = image.thumbnailUrl || image.imageUrl;
            const cacheBustedUrl = addCacheBuster(imageUrl, imageRefreshKey);
            return (
              <div
                key={image.id}
                className="review-item__image"
                onClick={() => onImageClick && onImageClick(review.images, index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onImageClick && onImageClick(review.images, index);
                  }
                }}
              >
                <img
                  src={cacheBustedUrl}
                  alt={`Attachment ${index + 1} from ${review.userName}'s review`}
                  loading="lazy"
                />
              </div>
            );
          })}
          {review.images.length > 3 && (
            <div className="review-item__image review-item__image--more">
              +{review.images.length - 3} more
            </div>
          )}
        </div>
      )}

      {/* Rejection Reason (if rejected) */}
      {review.status === 'REJECTED' && review.rejectionReason && (
        <div className="review-item__rejection">
          <i className="fas fa-info-circle"></i>
          <span>Reason: {review.rejectionReason}</span>
        </div>
      )}

      {/* Actions */}
      <div className="review-item__actions">
        <div className="review-item__actions-left">
          {/* Helpful Button - will be replaced with HelpfulButton component later */}
          <button
            className={`review-item__helpful ${review.hasVoted ? 'review-item__helpful--voted' : ''}`}
            onClick={() => onHelpfulClick && onHelpfulClick(review.id)}
            disabled={!currentUserId}
            title={!currentUserId ? 'Sign in to vote' : undefined}
          >
            <i className="fas fa-thumbs-up"></i>
            Helpful ({review.helpfulCount})
          </button>
        </div>

        {isOwnReview && (
          <div className="review-item__actions-right">
            <button
              className="review-item__action-btn review-item__action-btn--edit"
              onClick={() => onEditClick && onEditClick(review)}
              disabled={review.status === 'PENDING'}
              title={review.status === 'PENDING' ? 'Cannot edit while pending approval' : 'Edit review'}
            >
              <i className="fas fa-edit"></i> Edit
            </button>
            <button
              className="review-item__action-btn review-item__action-btn--delete"
              onClick={() => onDeleteClick && onDeleteClick(review)}
            >
              <i className="fas fa-trash"></i> Delete
            </button>
          </div>
        )}
      </div>
    </article>
  );
});

ReviewItem.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.number.isRequired,
    userName: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    title: PropTypes.string,
    comment: PropTypes.string,
    isVerifiedPurchase: PropTypes.bool,
    helpfulCount: PropTypes.number,
    hasVoted: PropTypes.bool,
    status: PropTypes.oneOf(['PENDING', 'APPROVED', 'REJECTED', 'FLAGGED']),
    images: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      imageUrl: PropTypes.string,
      thumbnailUrl: PropTypes.string
    })),
    createdAt: PropTypes.string.isRequired,
    rejectionReason: PropTypes.string
  }).isRequired,
  isOwnReview: PropTypes.bool,
  currentUserId: PropTypes.number,
  onHelpfulClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onImageClick: PropTypes.func,
  imageRefreshKey: PropTypes.number
};

export default ReviewItem;
